/* eslint-disable factorial/no-raw-html */
import React, { useRef, useState } from 'react'
import { Document, Page, PasswordResponses } from 'react-pdf'
import { OnPasswordCallback, PasswordResponse } from 'react-pdf/dist/cjs/shared/types'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

import { Icon } from '@factorialco/deprecated-design-system'
import classNames from 'classnames'
import { debounce, throttle } from 'lodash'
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import EnableEditModal from './EnableEditModal'
import LoadingSpinner from './LoadingSpinner'
import Saving from './Saving'
import ScaleSelector from './ScaleSelector'
import ToolbarButton from './ToolbarButton'
import * as styles from './index.vanilla.css'
import { downloadPdf, getPdfBlob, printPdf } from './pdfUtils'
import { ControllerType, ScaleType, Scales } from './types'
import { calculateVisiblePage } from './viewUtils'

const SAVE_DELAY_MS = 1000
const ON_SCROLL_DELAY_MS = 100

interface Annotation {
  id: string
  subtype: string
  value?: boolean
  checked?: boolean
  checkBox?: boolean
  radioButton?: boolean
}

type AnnotationPagesMap = { [key: number]: Annotation[] }

// We feed the interface with these fields that we know exist but that the library doesn't
// add in its type to avoid false errors when using them
interface ExtendedPDFPageProxy extends PDFPageProxy {
  originalHeight: number
  originalWidth: number
}

export interface Props {
  url: string
  filename: string
  fillable?: boolean
  page?: number
  pagesToDisplay?: number[]
  initialScale?: ScaleType
  controllers?: ControllerType[]
  onPdfLoaded?: (pdf: PDFDocumentProxy) => void
  onSaveFile?: (blob: Blob) => Promise<void>
  onSavingStatusChange?: (value: boolean) => void
  onPrintPdf?: () => void
  onDownloadPdf?: () => void
  onCloseFillableModal?: () => void
  onPageChange?: (page: number) => void
}

const PdfViewer = (props: Props) => {
  const {
    url,
    onSaveFile,
    fillable = false,
    onCloseFillableModal,
    pagesToDisplay = [],
    page = 0,
    controllers = ['pages', 'zoom', 'actions'],
    onPageChange,
    initialScale = 'page-width',
  } = props
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null)
  const [pages, setPages] = useState<ExtendedPDFPageProxy[]>([])
  const [scale, setScale] = useState(1)
  const [nextPage, setNextPage] = useState<number | null>(null)
  const toolbarElement = useRef<HTMLDivElement>(null)
  const containerElement = useRef<HTMLDivElement>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [editing, setEditing] = useState(false)
  const [scrollHeight, setScrollHeight] = useState(0)
  const [saving, setSaving] = useState<boolean>(false)
  const pageElements = useRef<(HTMLElement | null)[]>([])
  const annotationPagesMap = useRef<AnnotationPagesMap>({})
  const currentPageRef = useRef(currentPage)
  currentPageRef.current = currentPage
  const fillableRef = useRef(fillable)
  fillableRef.current = fillable
  const toolbarHeight = toolbarElement.current?.offsetHeight || 0
  const [skipPassword, setSkipPassword] = useState<boolean>(false)
  const totalPages = pagesToDisplay.length > 0 ? pagesToDisplay.length : pdf?.numPages

  useEffect(() => setScrollHeight(containerElement.current?.scrollHeight || 0), [scale])

  useEffect(() => props?.onSavingStatusChange && props!.onSavingStatusChange(saving), [saving])

  useEffect(() => {
    onPageChange && onPageChange(currentPage)
  }, [currentPage])

  useEffect(() => {
    if (editing) {
      onCloseFillableModal && onCloseFillableModal()
    }
  }, [editing])

  useEffect(() => {
    goToPage(page)
  }, [page])

  const onPageFit = (page: ExtendedPDFPageProxy, parentElement: HTMLElement): number => {
    const pageHeight = page.originalHeight
    const containerHeight = parentElement.clientHeight - toolbarHeight - 20
    return containerHeight / pageHeight
  }

  const onPageWidth = (page: ExtendedPDFPageProxy, parentElement: HTMLElement): number => {
    const pageWidth = page.originalWidth
    const containerWidth = parentElement.clientWidth - 20
    return containerWidth / pageWidth
  }

  const onPageAdjust = (
    value: ScaleType,
    callback: (pdfPage: ExtendedPDFPageProxy, parentElement: HTMLElement) => number
  ) => {
    const pageIndex = currentPageRef.current - 1

    if (pageIndex < 0 || !pages.length) {
      setTimeout(() => onPageAdjust(value, callback), 100)
      return
    }

    const pdfPage = pages[pageIndex]

    const parentElement = containerElement.current
    if (parentElement) {
      const expectedScale = callback(pdfPage!, parentElement!)
      setScale(expectedScale)
    }
    setSelectedScale(value)

    setNextPage(pageIndex)
  }

  const setScaleFromValue = (value: ScaleType): void => {
    setScale(Number(value))
    setSelectedScale(value)
  }

  const scales: Scales[] = [
    {
      label: i18n.t('documents.viewer.page_width'),
      value: 'page-width',
      callback: (value: ScaleType) => onPageAdjust(value, onPageWidth),
    },
    {
      label: i18n.t('documents.viewer.page_fit'),
      value: 'page-fit',
      callback: (value: ScaleType) => onPageAdjust(value, onPageFit),
    },
    {
      label: '50%',
      value: '0.5',
      callback: setScaleFromValue,
    },
    {
      label: '75%',
      value: '0.75',
      callback: setScaleFromValue,
    },
    {
      label: '100%',
      value: '1',
      callback: setScaleFromValue,
    },
    {
      label: '125%',
      value: '1.25',
      callback: setScaleFromValue,
    },
    {
      label: '150%',
      value: '1.5',
      callback: setScaleFromValue,
    },
    {
      label: '200%',
      value: '2',
      callback: setScaleFromValue,
    },
    {
      label: '300%',
      value: '3',
      callback: setScaleFromValue,
    },
    {
      label: '400%',
      value: '4',
      callback: setScaleFromValue,
    },
  ]

  const defaultScale = scales.find((s) => s.value === initialScale)?.value || 'page-width'
  const [selectedScale, setSelectedScale] = React.useState<ScaleType>(defaultScale)

  const onChangeScale = (scaleValue: ScaleType) => {
    const scaleOption = scales.find((s) => s.value === scaleValue)

    scaleOption?.callback(scaleValue)
  }

  const file = useMemo(
    () => ({
      url,
      withCredentials: true,
    }),
    [url]
  )

  useEffect(() => {
    setPdf(null)
    annotationPagesMap.current = {}
    pageElements.current = []
    setSkipPassword(false)
  }, [url])

  useEffect(() => {
    if (nextPage === null) return

    goToPage(nextPage)

    setNextPage(null)
  }, [nextPage])

  const onDocumentLoadSuccess = (pdf: PDFDocumentProxy) => {
    setPdf(pdf)

    if (props.onPdfLoaded) props.onPdfLoaded(pdf)

    onChangeScale(selectedScale!)

    setTimeout(() => {
      setCurrentPage(page + 1)

      goToPage(page)
    }, 0)
  }

  const savingFunction = useUnsafeMemo(
    () =>
      debounce(async () => {
        if (!pdf) return

        const blob = await getPdfBlob(pdf!)

        await onSaveFile!(blob)

        setSaving(false)
      }, SAVE_DELAY_MS),
    [pdf, onSaveFile]
  )

  const saveAnnotation = async (annotationId: string, value: string | boolean | null) => {
    if (!onSaveFile || !fillableRef.current) {
      return
    }

    pdf!.annotationStorage.setValue(annotationId, { value })

    setSaving(true)

    savingFunction()
  }

  const onGenericAnnotationChanged = (
    event: React.FormEvent<HTMLInputElement>,
    annotation: Annotation
  ) => {
    saveAnnotation(annotation.id, event.currentTarget.value)
  }

  const onCheckboxAnnotationChanged = (
    event: React.FormEvent<HTMLInputElement>,
    annotation: Annotation
  ) => {
    const { id, checked } = event.currentTarget

    setTimeout(() => {
      const updatedElement: HTMLInputElement = document.getElementById(id) as HTMLInputElement
      saveAnnotation(annotation.id, checked)
      updatedElement!.checked = checked
    }, 0)
  }

  const onRadioButtonAnnotationChanged = (
    event: React.FormEvent<HTMLInputElement>,
    annotation: Annotation
  ) => {
    const { name } = event.currentTarget

    setTimeout(() => {
      const radioButtons: NodeList = document.getElementsByName(name) as NodeList

      radioButtons.forEach((node) => {
        const radioButtonNode = node as HTMLInputElement
        const id = radioButtonNode.dataset.elementId!
        const checked = id === annotation.id
        const textValue = checked ? 'on' : 'off'
        const annotationValue = checked ? 'on' : null

        radioButtonNode.checked = checked
        radioButtonNode.value = textValue
        saveAnnotation(id, annotationValue)
      })
    }, 0)
  }

  const onGetAnnotationsSuccess = (annotations: Annotation[], pageIndex: number) => {
    annotationPagesMap.current[pageIndex] = annotations
  }

  const onRenderAnnotationLayerSuccess = (pageIndex: number) => {
    const annotations = annotationPagesMap.current[pageIndex] || []

    annotations.forEach(initializeAnnotation)
  }

  const initializeAnnotation = (annotation: Annotation) => {
    if (annotation.subtype !== 'Widget') return

    const element = document.querySelector(
      `[data-element-id="${annotation.id}"]`
    ) as HTMLFormElement

    if (!element) return

    const { value } = pdf!.annotationStorage.getValue(annotation.id, {
      value: null,
    }) as { value: string }

    let listener = onGenericAnnotationChanged

    if (annotation.checkBox) {
      listener = onCheckboxAnnotationChanged
      if (value !== null) element.checked = value
    } else if (annotation.radioButton) {
      listener = onRadioButtonAnnotationChanged
      if (value !== null) element.value = value
    } else {
      if (value !== null) element.value = value
    }

    if (!fillable) {
      element.tabIndex = -1
      return
    }

    element.addEventListener('input', (event) =>
      listener(event as unknown as React.FormEvent<HTMLInputElement>, annotation)
    )
  }

  const onContainerScroll = (event: React.BaseSyntheticEvent) => {
    setScrollHeight(event.target.scrollHeight)

    const visiblePage = calculateVisiblePage(event, pageElements.current, toolbarHeight)

    if (!visiblePage) return

    setCurrentPage(visiblePage)
  }

  const onScroll = useMemo(() => throttle(onContainerScroll, ON_SCROLL_DELAY_MS), [])

  const increaseScale = () =>
    changeScaleToNextStep(scales, (scaleValue, scale) => scaleValue > scale)
  const decreaseScale = () =>
    changeScaleToNextStep([...scales].reverse(), (scaleValue, scale) => scaleValue < scale)

  const changeScaleToNextStep = (
    scaleOptions: typeof scales,
    compareScales: (scale: number, currentScale: number) => boolean
  ) => {
    const newScale = scaleOptions.find((scaleOption) => {
      const scaleValue = Number(scaleOption.value)

      return compareScales(scaleValue, scale)
    })

    newScale?.callback(newScale.value)
  }

  useUnsafeMemo(() => {
    if (!pdf) setPages([])

    setEditing(false)
    setSaving(false)
    setScrollHeight(0)
  }, [pdf])

  const pagesList = useUnsafeMemo(() => {
    if (!pdf) return null

    return Array.from(Array(totalPages)).map((_, index) => (
      <div
        key={index}
        className={styles.pdfViewerPage}
        style={{ '--scale-factor': scale } as React.CSSProperties}
      >
        <Page
          renderForms
          renderAnnotationLayer
          renderTextLayer
          onGetAnnotationsSuccess={(annotations) => onGetAnnotationsSuccess(annotations, index)}
          onRenderAnnotationLayerSuccess={() => onRenderAnnotationLayerSuccess(index)}
          pageNumber={((pagesToDisplay.length > 0 ? pagesToDisplay[index] : index) || 0) + 1}
          scale={scale}
          inputRef={(reference) => {
            pageElements.current[index] = reference
          }}
          onLoadSuccess={(pdfPage: ExtendedPDFPageProxy) => {
            pages[index] = pdfPage
            setPages([...pages])
          }}
        />
      </div>
    ))
  }, [pdf, scale, pagesToDisplay])

  const goToPage = useUnsafeCallback(
    (targetPageIndex: number) => {
      if (!pdf) return

      let destinationPageIndex: number = targetPageIndex
      const lastPageIndex = (totalPages || 1) - 1

      if (destinationPageIndex > lastPageIndex) {
        destinationPageIndex = lastPageIndex
      } else if (destinationPageIndex < 0) {
        destinationPageIndex = 0
      }

      const element = pageElements.current[destinationPageIndex]!
      const parentContainer = element?.offsetParent
      if (parentContainer) {
        parentContainer.scrollTop = element.offsetTop - toolbarHeight - 10
      }
    },
    [pdf]
  )

  const onPrintPdf = () => {
    printPdf(pdf)
    if (props.onPrintPdf) props.onPrintPdf()
  }

  const onDownloadPdf = () => {
    downloadPdf(pdf, props.filename)
    if (props.onDownloadPdf) props.onDownloadPdf()
  }

  const onHandlePassword = (callback: OnPasswordCallback, reason: PasswordResponse) => {
    const callbackProxy = (password: string | null) => {
      if (password === null) {
        setSkipPassword(true)
        return
      }

      callback(password)
    }

    switch (reason) {
      case PasswordResponses.NEED_PASSWORD: {
        const password = prompt(i18n.t('documents.viewer.password_enter'))
        callbackProxy(password)
        break
      }
      case PasswordResponses.INCORRECT_PASSWORD: {
        const password = prompt(i18n.t('documents.viewer.password_invalid'))
        callbackProxy(password)
        break
      }
      default:
    }
  }

  const showSpinner = !pdf
  const showBlockingOverlay = !editing || !fillable
  const showEditingModal = !showSpinner && showBlockingOverlay && fillable
  const showPagesController = controllers.includes('pages')
  const showZoomController = controllers.includes('zoom')
  const showActionsController = controllers.includes('actions')

  return (
    <div className={styles.pdfViewerContainer}>
      <div className={styles.pdfViewerInternalContainer} ref={containerElement} onScroll={onScroll}>
        {controllers.length > 0 && (
          <div ref={toolbarElement} className={styles.pdfViewerToolbar}>
            {showPagesController && (
              <div className={styles.pdfViewerToolbarSection}>
                <ToolbarButton
                  ariaLabel={i18n.t('documents.viewer.previous_page')}
                  iconName="chevron-up"
                  onClick={() => goToPage(currentPage - 2)}
                />
                <ToolbarButton
                  ariaLabel={i18n.t('documents.viewer.next_page')}
                  iconName="chevron-down"
                  onClick={() => goToPage(currentPage)}
                />
                {pdf?.numPages && (
                  <div className={styles.pdfViewerPagination}>
                    {currentPage} / {totalPages}
                  </div>
                )}
              </div>
            )}

            {showZoomController && (
              <div className={styles.pdfViewerToolbarSection}>
                <ToolbarButton
                  ariaLabel={i18n.t('documents.viewer.zoom_out')}
                  iconName="zoom-out"
                  onClick={decreaseScale}
                />
                <ToolbarButton
                  ariaLabel={i18n.t('documents.viewer.zoom_in')}
                  iconName="zoom-in"
                  onClick={increaseScale}
                />
                <div className={styles.pdfViewerSelectorContainer}>
                  <div className={styles.pdfViewerZoomIcon}>
                    <Icon.Small color="grey000White" icon="caret-double" />
                  </div>
                  <ScaleSelector value={selectedScale} onChange={onChangeScale} options={scales} />
                </div>
              </div>
            )}

            {showActionsController && (
              <div className={styles.pdfViewerToolbarSection}>
                <Saving saving={saving} />

                <ToolbarButton
                  ariaLabel={i18n.t('documents.viewer.print')}
                  iconName="printer"
                  onClick={onPrintPdf}
                />

                <ToolbarButton
                  ariaLabel={i18n.t('documents.viewer.download')}
                  iconName="download"
                  onClick={onDownloadPdf}
                />
              </div>
            )}
          </div>
        )}

        {showSpinner && <LoadingSpinner />}

        <div>
          {showBlockingOverlay && (
            <div
              className={classNames({
                [styles.pdfBlockingOverlay]: true,
                [styles.pdfFillableOverlay]: fillable,
              })}
              style={{
                height: scrollHeight || '100%',
              }}
            ></div>
          )}

          {url && !skipPassword && (
            <Document
              file={file}
              onLoadSuccess={onDocumentLoadSuccess}
              onPassword={onHandlePassword}
              loading={<LoadingSpinner />}
            >
              {pagesList}
            </Document>
          )}
        </div>

        {showEditingModal && <EnableEditModal onSubmit={() => setEditing(true)} />}
      </div>
    </div>
  )
}

export default PdfViewer
